import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClinicResources } from "../../store/thunks/resourceThunk";
import { fetchClinics } from "../../store/thunks/clinicThunk";
import ResourceFormModal from "../../components/ResourceFormModal";
import Table from "../../applicationUi/components/Table";

function ResourcePage() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const resources = useSelector((state) => state.resources.resources);
  const clinics = useSelector((state) => state.clinics.clinics);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedClinic, setSelectedClinic] = useState(null);

  useEffect(() => {
    // Fetch all clinics first
    dispatch(fetchClinics());
  }, [dispatch]);

  useEffect(() => {
    // Set default clinic and fetch resources
    if (clinics.length > 0 && !selectedClinic) {
      const defaultClinic = clinics.find(clinic => clinic._id === user?.defaultLocation) || clinics[0];
      setSelectedClinic(defaultClinic);
    }
  }, [clinics, user?.defaultLocation, selectedClinic]);

  useEffect(() => {
    // Fetch resources when clinic is selected
    if (selectedClinic?._id) {
      dispatch(fetchClinicResources(selectedClinic._id));
    }
  }, [dispatch, selectedClinic]);

  const handleAddResource = () => {
    setSelectedResource(null);
    setIsModalOpen(true);
  };

  const handleEditResource = (resourceId) => {
    const resource = resources.find(r => r._id === resourceId);
    setSelectedResource(resource);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedResource(null);
  };

  const handleModalSave = () => {
    if (selectedClinic?._id) {
      dispatch(fetchClinicResources(selectedClinic._id));
    }
  };

  const handleClinicChange = (event) => {
    const clinicId = event.target.value;
    const clinic = clinics.find(c => c._id === clinicId);
    setSelectedClinic(clinic);
  };

  // Define table columns
  const columns = [
    {
      header: "Name",
      accessor: "name",
      sortable: true,
    },
    {
      header: "Type",
      accessor: "type",
      sortable: true,
    },
    {
      header: "Description",
      accessor: "description",
      sortable: false,
    },
    {
      header: "Status",
      accessor: (resource) => (
        <span
          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
            resource.status === "active"
              ? "bg-green-100 text-green-800"
              : resource.status === "maintenance"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-red-100 text-red-800"
          }`}>
          {resource.status.charAt(0).toUpperCase() + resource.status.slice(1)}
        </span>
      ),
      sortable: true,
    },
  ];

  // Define table actions
  const actions = [
    {
      label: "Add Resource",
      onClick: handleAddResource,
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Clinic Selector */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Resources</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage resources for your selected clinic
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <label htmlFor="clinic-select" className="text-sm font-medium text-gray-700">
              Clinic:
            </label>
            <select
              id="clinic-select"
              value={selectedClinic?._id || ''}
              onChange={handleClinicChange}
              className="min-w-48 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select a clinic...</option>
              {clinics.map((clinic) => (
                <option key={clinic._id} value={clinic._id}>
                  {clinic.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedClinic ? (
        <Table
          title={`Resources - ${selectedClinic.name}`}
          description={`A list of all resources in ${selectedClinic.name} including their name, type, description, and status.`}
          columns={columns}
          data={resources}
          actions={actions}
          onRowClick={handleEditResource}
          showPagination={false}
        />
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500">Please select a clinic to view resources.</div>
        </div>
      )}

      <ResourceFormModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        mode={selectedResource ? "edit" : "add"}
        resource={selectedResource}
      />
    </div>
  );
}

export default ResourcePage;
